/**
 * Local-storage draft persistence for the Item Detail editor (#145).
 *
 * A safety net for unsaved edits (browser crash / accidental tab close) — NOT
 * a replacement for the actual save. Keyed per item id so switching items
 * doesn't clobber another item's draft.
 */

const DRAFT_PREFIX = 'rmdv_item_draft_';

const isStorageAvailable = () => {
  try {
    const test = '__rmdv_draft_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

const keyFor = (itemId) => `${DRAFT_PREFIX}${itemId}`;

/** Save a draft `{title, description, attributes}` for `itemId`, stamped with `savedAt`. */
export function saveDraft(itemId, draft) {
  if (!itemId || !isStorageAvailable()) return;
  try {
    localStorage.setItem(keyFor(itemId), JSON.stringify({ ...draft, savedAt: Date.now() }));
  } catch {
    // Best-effort only (e.g. quota exceeded) — never block editing on this.
  }
}

/** Load the draft for `itemId`, or null if none/corrupt. */
export function loadDraft(itemId) {
  if (!itemId || !isStorageAvailable()) return null;
  try {
    const raw = localStorage.getItem(keyFor(itemId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Discard the draft for `itemId` (call on successful save or explicit discard). */
export function clearDraft(itemId) {
  if (!itemId || !isStorageAvailable()) return;
  try {
    localStorage.removeItem(keyFor(itemId));
  } catch {
    // Ignore — nothing more to do if removal fails.
  }
}
